import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Fab,
  Breadcrumbs,
  Link,
  Divider,
  LinearProgress,
  InputAdornment,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Science as TestTubeIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Circle as CircleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gatewayWhatsappService } from '../services/gatewayWhatsappService';
import GatewayWhatsappTypeIcon from '../components/icons/GatewayWhatsappTypeIcon';
import { GatewayWhatsapp, GatewayWhatsappFormData } from '../types';
import { GatewayWhatsappForm } from '../components/forms';
import Swal from 'sweetalert2';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
} from '../styles/theme';

type GatewayWhatsappType = 'whaticket' | 'kangaroo' | 'local';

interface GatewayWhatsappPageState {
  gateways: GatewayWhatsapp[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  selectedType: GatewayWhatsappType | '';
  showOnlyActive: boolean;
}

interface MenuState {
  anchorEl: HTMLElement | null;
  gateway: GatewayWhatsapp | null;
}

interface DialogState {
  gatewayForm: boolean;
  confirmDelete: boolean;
  selectedGateway: GatewayWhatsapp | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const initialState: GatewayWhatsappPageState = {
  gateways: [],
  loading: false,
  error: null,
  searchTerm: '',
  selectedType: '',
  showOnlyActive: false,
};

const GatewayWhatsappPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [state, setState] = useState<GatewayWhatsappPageState>(initialState);
  const [menu, setMenu] = useState<MenuState>({
    anchorEl: null,
    gateway: null,
  });
  const [dialogs, setDialogs] = useState<DialogState>({
    gatewayForm: false,
    confirmDelete: false,
    selectedGateway: null,
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [testingConnections, setTestingConnections] = useState<Set<number>>(new Set());

  // Carregar gateways WhatsApp
  useEffect(() => {
    loadGatewaysWhatsapp();
  }, []);

  const loadGatewaysWhatsapp = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await gatewayWhatsappService.getAllGatewaysWhatsapp();
      setState(prev => ({ ...prev, gateways: response.gateways, loading: false }));
    } catch (error: any) {
      console.error('Erro ao carregar gateways WhatsApp:', error);
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message || 'Erro ao carregar gateways WhatsApp' 
      }));
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Menu
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, gateway: GatewayWhatsapp) => {
    setMenu({ anchorEl: event.currentTarget, gateway });
  };

  const handleCloseMenu = () => {
    setMenu({ anchorEl: null, gateway: null });
  };

  // Formulário
  const handleOpenForm = (gateway?: GatewayWhatsapp) => {
    setDialogs(prev => ({ 
      ...prev, 
      gatewayForm: true, 
      selectedGateway: gateway || null 
    }));
    handleCloseMenu();
  };

  const handleCloseForm = () => {
    setDialogs(prev => ({ 
      ...prev, 
      gatewayForm: false, 
      selectedGateway: null 
    }));
  };

  const handleSubmitForm = async (formData: GatewayWhatsappFormData) => {
    try {
      setFormLoading(true);
      
      if (dialogs.selectedGateway) {
        await gatewayWhatsappService.updateGatewayWhatsapp(dialogs.selectedGateway.id, formData);
        showSnackbar('Gateway WhatsApp atualizado com sucesso!');
      } else {
        await gatewayWhatsappService.createGatewayWhatsapp(formData);
        showSnackbar('Gateway WhatsApp criado com sucesso!');
      }
      
      handleCloseForm();
      loadGatewaysWhatsapp();
    } catch (error: any) {
      console.error('Erro ao salvar gateway WhatsApp:', error);
      showSnackbar(error.message || 'Erro ao salvar gateway WhatsApp', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Exclusão
  const handleOpenDeleteDialog = (gateway: GatewayWhatsapp) => {
    setDialogs(prev => ({ 
      ...prev, 
      confirmDelete: true, 
      selectedGateway: gateway 
    }));
    handleCloseMenu();
  };

  const handleDeleteGateway = async () => {
    if (!dialogs.selectedGateway) return;

    try {
      await gatewayWhatsappService.deleteGatewayWhatsapp(dialogs.selectedGateway.id);
      showSnackbar('Gateway WhatsApp excluído com sucesso!');
      setDialogs(prev => ({ ...prev, confirmDelete: false, selectedGateway: null }));
      loadGatewaysWhatsapp();
    } catch (error: any) {
      console.error('Erro ao excluir gateway WhatsApp:', error);
      showSnackbar(error.message || 'Erro ao excluir gateway WhatsApp', 'error');
    }
  };

  // Toggle status
  const handleToggleStatus = async (gateway: GatewayWhatsapp) => {
    try {
      await gatewayWhatsappService.toggleGatewayWhatsappStatus(gateway.id);
      showSnackbar(`Gateway WhatsApp ${gateway.ativo ? 'desativado' : 'ativado'} com sucesso!`);
      loadGatewaysWhatsapp();
      handleCloseMenu();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      showSnackbar(error.message || 'Erro ao alterar status', 'error');
    }
  };

  // Definir como padrão
  const handleSetAsDefault = async (gateway: GatewayWhatsapp) => {
    try {
      await gatewayWhatsappService.setGatewayWhatsappAsDefault(gateway.id);
      showSnackbar('Gateway WhatsApp definido como padrão com sucesso!');
      loadGatewaysWhatsapp();
      handleCloseMenu();
    } catch (error: any) {
      console.error('Erro ao definir como padrão:', error);
      showSnackbar(error.message || 'Erro ao definir como padrão', 'error');
    }
  };

  // Testar conectividade
  const handleTestConnection = async (gateway: GatewayWhatsapp) => {
    try {
      // Passo 1: Obter número de telefone do usuário
      const phoneResult = await Swal.fire({
        title: 'Teste de Conectividade',
        input: 'text',
        inputLabel: 'Digite o número de telefone para teste',
        inputPlaceholder: 'Exemplo: 11999999999',
        text: `Será enviada uma mensagem de teste para o número informado usando o gateway "${gateway.nome}".`,
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return 'Por favor, digite um número de telefone válido';
          }
          const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ''))) {
            return 'Formato de telefone inválido. Use apenas números e +';
          }
        },
        showConfirmButton: true,
        confirmButtonText: 'Enviar WhatsApp de Teste',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (!phoneResult.isConfirmed || !phoneResult.value) {
        return;
      }

      const phoneNumber = phoneResult.value;
      setTestingConnections(prev => new Set(prev).add(gateway.id));

      try {
        // Passo 2: Enviar WhatsApp de teste
        const response = await gatewayWhatsappService.testGatewayWhatsappConnection(gateway.id, phoneNumber);
        
        if (response.success) {
          // WhatsApp enviado com sucesso - Pedir confirmação do usuário
          const confirmResult = await Swal.fire({
            title: 'WhatsApp Enviado!',
            text: response.message,
            icon: 'info',
            html: `
              <p>${response.message}</p>
              <br>
              <strong>Você recebeu a mensagem de teste no número ${phoneNumber}?</strong>
            `,
            showConfirmButton: true,
            confirmButtonText: 'Sim, recebi',
            showCancelButton: true,
            cancelButtonText: 'Não recebi',
            reverseButtons: true,
          });

          if (confirmResult.isConfirmed) {
            // Passo 3: Confirmar o teste
            const confirmResponse = await gatewayWhatsappService.testGatewayWhatsappConnection(gateway.id, 'confirmar');
            
            if (confirmResponse.success) {
              await Swal.fire({
                title: 'Sucesso!',
                text: confirmResponse.message,
                icon: 'success',
                confirmButtonText: 'OK',
              });
            } else {
              await Swal.fire({
                title: 'Erro na Confirmação',
                text: confirmResponse.message,
                icon: 'error',
                confirmButtonText: 'OK',
              });
            }
            
            loadGatewaysWhatsapp();
          } else {
            // Usuário não recebeu a mensagem
            await Swal.fire({
              title: 'Conectividade com Problemas',
              html: `
                <p>O WhatsApp de teste não foi recebido.</p>
                <br>
                <strong>Possíveis causas:</strong>
                <ul style="text-align: left; margin: 10px 0;">
                  <li>Número de telefone incorreto</li>
                  <li>Problemas na configuração do gateway</li>
                  <li>Falha na conectividade com o provedor WhatsApp</li>
                  <li>Demora na entrega da mensagem</li>
                </ul>
                <p>Verifique as configurações do gateway ou entre em contato com o suporte.</p>
              `,
              icon: 'warning',
              confirmButtonText: 'Entendi',
            });
          }
        } else {
          // Erro no envio do WhatsApp
          let errorDetails = '';
          if (response.error && response.statusCode) {
            errorDetails = `
              <div style="margin-top: 15px; text-align: left;">
                <strong>📋 Detalhes do Erro:</strong>
                <div style="background: #f5f5f5; padding: 10px; border-radius: 4px; font-size: 12px; margin-top: 5px;">
                  <strong>Status:</strong> ${response.statusCode}<br>
                  <strong>Erro:</strong> ${response.error}
                </div>
              </div>
            `;
          }
          
          await Swal.fire({
            title: 'Erro no Teste',
            html: `
              <div style="text-align: left;">
                <p><strong>❌ Erro:</strong> ${response.message}</p>
                ${errorDetails}
              </div>
            `,
            icon: 'error',
            confirmButtonText: 'OK',
            width: errorDetails ? '500px' : '400px',
          });
          loadGatewaysWhatsapp();
        }
        
      } catch (error: any) {
        console.error('Erro ao testar conectividade:', error);
        
        await Swal.fire({
          title: 'Erro Inesperado',
          text: 'Ocorreu um erro inesperado ao testar a conectividade. Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        setTestingConnections(prev => {
          const newSet = new Set(prev);
          newSet.delete(gateway.id);
          return newSet;
        });
        handleCloseMenu();
      }
      
    } catch (error: any) {
      console.error('Erro no fluxo de teste:', error);
      showSnackbar('Erro no fluxo de teste de conectividade', 'error');
    }
  };

  // Filtros
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleTypeFilter = (type: GatewayWhatsappType | '') => {
    setState(prev => ({ ...prev, selectedType: type }));
  };

  const handleActiveFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, showOnlyActive: event.target.checked }));
  };

  // Filtrar gateways WhatsApp
  const filteredGateways = state.gateways.filter(gateway => {
    const matchesSearch = !state.searchTerm || 
      gateway.nome.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      gateway.observacoes?.toLowerCase().includes(state.searchTerm.toLowerCase());
      
    const matchesType = !state.selectedType || gateway.tipo === state.selectedType;
    const matchesStatus = !state.showOnlyActive || gateway.ativo;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Estatísticas rápidas
  const stats = {
    total: state.gateways.length,
    ativos: state.gateways.filter(g => g.ativo).length,
    inativos: state.gateways.filter(g => !g.ativo).length,
    padrao: state.gateways.filter(g => g.is_default).length,
  };

  // Verificar permissões
  const canManageGateways = ['administrador', 'financeiro'].includes(user?.perfil || '');

  if (state.loading && state.gateways.length === 0) {
    return (
      <Box sx={{ ...pageShellSx, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 1 }}>
        <CircularProgress size={32} />
        <Typography variant="caption" color="text.secondary">Carregando gateways WhatsApp...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'success')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <WhatsAppIcon sx={{ fontSize: 120, color: theme.palette.success.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.35)}` }}>
              <WhatsAppIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gateways WhatsApp</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie gateways para envio de mensagens WhatsApp</Typography>
            </Box>
          </Stack>
          {canManageGateways && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenForm()} fullWidth={isMobile}>Novo Gateway WhatsApp</Button>
          )}
        </Stack>
      </Paper>

      {stats.total > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={stats.total} icone={<WhatsAppIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Ativos" valor={stats.ativos} icone={<CheckIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Inativos" valor={stats.inativos} icone={<CloseIcon sx={{ color: theme.palette.error.main }} />} cor={theme.palette.error.main} bgCor={alpha(theme.palette.error.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Padrão" valor={stats.padrao} icone={<StarIcon sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" placeholder="Buscar gateways..." value={state.searchTerm} onChange={handleSearch} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={state.selectedType} onChange={(e) => handleTypeFilter(e.target.value as GatewayWhatsappType | '')} label="Tipo">
                <MenuItem value="">Todos os tipos</MenuItem>
                <MenuItem value="whaticket">Whaticket</MenuItem>
                <MenuItem value="kangaroo"><Box display="flex" alignItems="center" gap={1}><GatewayWhatsappTypeIcon tipo="kangaroo" size={20} />Kangaroo</Box></MenuItem>
                <MenuItem value="local"><Box display="flex" alignItems="center" gap={1}><GatewayWhatsappTypeIcon tipo="local" size={20} />Local (Conexão Nativa)</Box></MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel control={<Switch checked={state.showOnlyActive} onChange={handleActiveFilter} color="primary" size="small" />} label={<Typography variant="body2">Apenas ativos</Typography>} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadGatewaysWhatsapp} disabled={state.loading}>Atualizar</Button>
          </Grid>
        </Grid>
      </Paper>

      {state.error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>{state.error}</Alert>}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Gateways WhatsApp</Typography>
          <Chip size="small" label={`${filteredGateways.length} gateway${filteredGateways.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>
        {state.loading && <LinearProgress />}
        {filteredGateways.length === 0 && !state.loading ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <WhatsAppIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              {state.searchTerm || state.selectedType || state.showOnlyActive ? 'Nenhum gateway encontrado com os filtros aplicados' : 'Nenhum gateway WhatsApp cadastrado'}
            </Typography>
            {canManageGateways && !state.searchTerm && !state.selectedType && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenForm()} sx={{ mt: 1.5 }}>Cadastrar Primeiro Gateway</Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={rankingTableHeadRowSx(theme)}>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Conectividade</TableCell>
              <TableCell align="center">Padrão</TableCell>
              <TableCell>Última Atualização</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGateways.map((gateway) => (
              <TableRow key={gateway.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                        {gateway.nome}
                      </Typography>
                      {gateway.observacoes && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {gateway.observacoes}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={gatewayWhatsappService.getGatewayWhatsappDisplayName(gateway.tipo)}
                      sx={{
                        backgroundColor: gatewayWhatsappService.getGatewayWhatsappColor(gateway.tipo) + '20',
                        color: gatewayWhatsappService.getGatewayWhatsappColor(gateway.tipo),
                        fontWeight: 'medium',
                      }}
                      icon={
                        <GatewayWhatsappTypeIcon tipo={gateway.tipo} size={18} />
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={gateway.ativo ? 'Ativo' : 'Inativo'}
                      color={gateway.ativo ? 'success' : 'error'}
                      variant="outlined"
                      icon={<CircleIcon />}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={gatewayWhatsappService.getStatusConnectivityLabel(gateway.status_conectividade)}
                      sx={{
                        backgroundColor: gatewayWhatsappService.getStatusConnectivityColor(gateway.status_conectividade) + '20',
                        color: gatewayWhatsappService.getStatusConnectivityColor(gateway.status_conectividade),
                      }}
                      icon={<CircleIcon />}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {gateway.is_default ? (
                      <Tooltip title="Gateway Padrão">
                        <StarIcon color="warning" />
                      </Tooltip>
                    ) : (
                      <StarBorderIcon color="disabled" />
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                      {new Date(gateway.updatedAt).toLocaleDateString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {new Date(gateway.updatedAt).toLocaleTimeString('pt-BR')}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    {canManageGateways && (
                      <IconButton
                        onClick={(e) => handleOpenMenu(e, gateway)}
                        size="small"
                        sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
        )}
      </Paper>

      {/* Menu de ações */}
      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleOpenForm(menu.gateway!)}>
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        
        <MenuItem onClick={() => handleToggleStatus(menu.gateway!)}>
          {menu.gateway?.ativo ? (
            <>
              <VisibilityOffIcon sx={{ mr: 1 }} />
              Desativar
            </>
          ) : (
            <>
              <VisibilityIcon sx={{ mr: 1 }} />
              Ativar
            </>
          )}
        </MenuItem>

        {!menu.gateway?.is_default && menu.gateway?.ativo && (
          <MenuItem onClick={() => handleSetAsDefault(menu.gateway!)}>
            <StarIcon sx={{ mr: 1 }} />
            Definir como Padrão
          </MenuItem>
        )}

        <MenuItem 
          onClick={() => handleTestConnection(menu.gateway!)}
          disabled={testingConnections.has(menu.gateway?.id || 0)}
        >
          <TestTubeIcon sx={{ mr: 1 }} />
          {testingConnections.has(menu.gateway?.id || 0) ? 'Testando...' : 'Testar Conectividade'}
        </MenuItem>

        <Divider />
        
        <MenuItem onClick={() => handleOpenDeleteDialog(menu.gateway!)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* FAB para dispositivos móveis */}
      {canManageGateways && isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 86, // Footer height (70px) + spacing (16px) = 86px
            right: 16,
            zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
          }}
          onClick={() => handleOpenForm()}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Formulário */}
      <GatewayWhatsappForm
        open={dialogs.gatewayForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        gateway={dialogs.selectedGateway}
        loading={formLoading}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={dialogs.confirmDelete}
        onClose={() => setDialogs(prev => ({ ...prev, confirmDelete: false }))}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o gateway WhatsApp "{dialogs.selectedGateway?.nome}"?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogs(prev => ({ ...prev, confirmDelete: false }))}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteGateway}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GatewayWhatsappPage;
